// @flow
import React, { Component } from "react";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { Formik, Form } from "formik";
import ErrorOutline from "elevate-ui-icons/ErrorOutline";
import Alert from "elevate-ui/Alert";
import Button from "elevate-ui/Button";
import Paper from "elevate-ui/Paper";
import withStyles from "elevate-ui/withStyles";
import transform from "lodash/transform";
import omit from "lodash/omit";
import isEqual from "lodash/isEqual";
import Effect from "../common/FormikEffect";

import FormSectionHeading from "../common/FormSectionHeading";
import FilterSection from "./FilterSection";
import LoginSignup from "./LoginSignup";
import FilterBottomBar from "./FilterBottomBar";
import FilterSectionTabs from "./FilterSectionTabs";
import ReviewFilters from "./ReviewFilters";

import type { $ReactRouter } from "flow/types";

type Props = {
  activeMLSFeeds: Array<number>,
  advancedSearchFilterOrder: Object,
  filters: Object,
  classes: Object,
  className?: string,
  cleanFilters: Function,
  esEnabled: boolean,
  formattedCount: string,
  getInitial: Function,
  handleFilterToggle: Function,
  handleSearch: (null | Event, Object) => void,
  isMapEnabled: boolean,
  mlsFeeds: Array<Object>,
  query: Object,
  visible: boolean,
  prepopulated: Object,
  siteUser: Object,
  location: $ReactRouter,
  mlsApiVersion: number,
};

type State = {
  filterButtonText: string,
  availableFilters: Object,
  steps: Array<string>,
  currentPropertyTypes: Array<string>,
  activeStep: string,
};

class FilterForm extends Component<Props, State> {
  constructor(props) {
    super(props);

    const availableFilters = {
      Basic: {},
      Location: {},
      Interior: {},
      Exterior: {},
      Community: {},
      Utilities: {},
      Other: {},
    };

    const steps = [];

    props.filters &&
      props.filters.forEach((filter) => {
        // Add filters returned from the API to their appropriate sections
        if (filter.section && availableFilters[filter.section]) {
          if (!props.siteUser && !filter.requiresES) {
            // If the user isn't logged in, they should only see filters that are marked as es not-required
            availableFilters[filter.section][filter.key] = filter;
          } else if (props.siteUser) {
            // If the user is logged in, give them access to all filters
            availableFilters[filter.section][filter.key] = filter;
          }
        }
      });

    Object.keys(availableFilters).forEach((section) => {
      if (!steps.includes(section) && !isEmpty(availableFilters[section])) {
        steps.push(section);
      }
    });

    steps.push("Review");

    this.state = {
      availableFilters,
      steps,
      filterButtonText: "Filter Listings",
      currentPropertyTypes: this.getPropertyTypesFromProps(props),
      activeStep: "Basic",
    };
  }

  // flow declaration for Formik Ref => `this.filterForm`
  filterForm: any;

  componentDidUpdate(prevProps) {
    if (prevProps.query.type !== this.props.query.type) {
      this.setState({
        currentPropertyTypes: this.getPropertyTypesFromProps(this.props),
      });
    }
  }

  getPropertyTypesFromProps({ query }) {
    return query.type
      ? Array.isArray(query.type)
        ? query.type
        : [query.type]
      : [];
  }

  updateCurrentPropertyTypes(currentPropertyTypes) {
    this.setState({ currentPropertyTypes });
  }

  handleNextSection = () => {
    const { steps, activeStep } = this.state;
    const currentIndex = steps.indexOf(activeStep);
    const nextStep = steps[currentIndex + 1];

    if (activeStep !== "Review") {
      return this.setState({ activeStep: nextStep });
    }

    return;
  };

  handleSectionSelect = (step) => {
    this.setState({ activeStep: step });
  };

  handleReset = () => {
    const { steps } = this.state;
    this.setState({ activeStep: steps[0] }, () => {
      this.props.handleSearch(null, {});
      this.filterForm.resetForm();
    });
  };

  render() {
    const {
      advancedSearchFilterOrder,
      filters,
      classes,
      className,
      cleanFilters,
      esEnabled,
      formattedCount,
      getInitial,
      handleFilterToggle,
      isMapEnabled,
      prepopulated,
      query,
      siteUser,
      visible,
      location,
    } = this.props;

    const {
      availableFilters,
      currentPropertyTypes,
      activeStep,
      steps,
    } = this.state;
    const initialFilterValues = transform(
      filters,
      (memo, filter) => {
        if (!filter.createRangeInputs) {
          memo[filter.name] = getInitial({ filter, query });
        } else {
          if (this.props.mlsApiVersion === 1) {
            memo[`min${filter.name}`] = getInitial({
              filter: { ...filter, name: `min${filter.name}` },
              query,
            });
            memo[`max${filter.name}`] = getInitial({
              filter: { ...filter, name: `max${filter.name}` },
              query,
            });
          } else {
            memo[`amin_${filter.name}`] = getInitial({
              filter: { ...filter, name: `amin_${filter.name}` },
              query,
            });
            memo[`amax_${filter.name}`] = getInitial({
              filter: { ...filter, name: `amax_${filter.name}` },
              query,
            });
          }
        }
      },
      {}
    );

    if (!visible) {
      return null;
    }

    return (
      <Paper
        className={classNames(
          classes.filterForm,
          visible ? classes.filterFormOpen : classes.filterFormClosed,
          className
        )}
        withPadding={false}
      >
        <FilterSectionTabs
          activeStep={activeStep}
          handleFilterToggle={handleFilterToggle}
          handleSectionSelect={this.handleSectionSelect}
          steps={steps}
        />
        <div className={classes.container}>
          <Formik
            enableReinitialize={true}
            initialValues={{
              ...initialFilterValues,
              sortName: query.sortName ? query.sortName : "",
              geoJson: query.geoJson ? query.geoJson : "",
            }}
            onSubmit={(values) => {
              this.props.handleSearch(null, cleanFilters(values));
            }}
            ref={(node) => (this.filterForm = node)}
            render={({ values }) => (
              <Form
                id="filterForm"
                name="filterForm"
                className={classes.form}
                method="get"
                action="/listings"
                noValidate
              >
                <Effect
                  onChange={(current, next) => {
                    if (
                      !isEqual(
                        cleanFilters(current.values),
                        cleanFilters(next.values)
                      )
                    ) {
                      this.props.handleSearch(null, cleanFilters(next.values));
                    }
                  }}
                />
                {["geoJson", "sortName"].map((field) => {
                  return (
                    <input key={field} id={field} name={field} type="hidden" />
                  );
                })}

                {Object.keys(availableFilters).map((sectionKey) => (
                  <FilterSection
                    key={sectionKey}
                    title={sectionKey}
                    currentPropertyTypes={currentPropertyTypes}
                    prepopulated={prepopulated}
                    sectionFilters={availableFilters[sectionKey]}
                    handleSearch={this.props.handleSearch}
                    query={this.props.query}
                    advancedSearchFilterOrder={advancedSearchFilterOrder}
                    visible={activeStep === sectionKey ? true : false}
                    mlsApiVersion={this.props.mlsApiVersion}
                  />
                ))}

                {activeStep === "Review" ? (
                  <ReviewFilters
                    handleSectionSelect={this.handleSectionSelect}
                    availableFilters={availableFilters}
                    cleanFilters={cleanFilters}
                    filterValues={values}
                    filters={filters}
                  />
                ) : null}

                {esEnabled && !isMapEnabled ? (
                  <FormSectionHeading
                    title={`Estimated results: ${formattedCount} listings`}
                  />
                ) : null}

                <Button
                  type="submit"
                  color="primary"
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    margin: "0 auto",
                  }}
                  onClick={() => {
                    this.props.handleFilterToggle();
                    window.scrollTo(0, 0);
                  }}
                >
                  See Results
                </Button>

                {query.geoJson ? (
                  <Alert
                    color="info"
                    icon={<ErrorOutline />}
                    style={{ marginTop: "24px" }}
                  >
                    <span>
                      Note: Results are currently limited to a selected
                      geographic area.
                    </span>
                    <Button
                      type="button"
                      isOutlined
                      onClick={() =>
                        this.props.handleSearch(
                          null,
                          omit(
                            {
                              ...this.props.query,
                            },
                            "geoJson"
                          )
                        )
                      }
                      style={{
                        marginLeft: "auto",
                      }}
                    >
                      Clear Boundaries
                    </Button>
                  </Alert>
                ) : null}

                {!siteUser ? <LoginSignup location={location} /> : null}
              </Form>
            )}
          />
        </div>
        <FilterBottomBar
          activeStep={activeStep}
          handleNextSection={this.handleNextSection}
          handleReset={this.handleReset}
          handleFilterToggle={handleFilterToggle}
        />
      </Paper>
    );
  }
}

export default withStyles((theme) => ({
  "@keyframes dropdown": {
    from: { overflow: "hidden", opacity: 0 },
    to: { overflow: "hidden", opacity: 1 },
  },
  filterForm: {
    display: "flex",
    margin: "0 auto",
    flexFlow: "column",
    justifyContent: "space-between",
    position: "fixed",
    left: "50%",
    top: "50%",
    width: "calc(100% - 40px)",
    transform: "translate(-50%, -50%)",
    maxWidth: "900px",
    height: "auto",
    maxHeight: "86vh",
    zIndex: theme.zIndex["modal"],
    overflow: "hidden",
    boxShadow:
      "0 6px 9px -2px rgba(0,0,0,.2), 0 9px 7px 0 rgba(0,0,0,0.01), 0 3px 4px 0 rgba(0,0,0,.02)",
    [theme.breakpoints(769)]: {
      maxHeight: "75vh",
    },
  },
  filterFormClosed: {
    maxHeight: "0",
    overflow: "hidden",
  },
  filterFormOpen: {
    overflow: "hidden",
    transition: "all 750ms ease",
    animationName: "dropdown",
    animationDuration: "750ms",
    animationTimingFunction: "ease",
    animationDirection: "forwards",
  },
  container: {
    overflowY: "scroll",
    "-webkit-overflow-scrolling": "touch",
    padding: "24px 24px 88px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  inputGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridColumnGap: "12px",
  },
}))(FilterForm);
